// Tabla de ordenes NO recibidas. Nuevas Solicitudes. Pantalla principal de cotizaciones
import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { FaRegEdit, FaFilePdf } from "react-icons/fa";
import { MdDelete, MdCheck, MdClose } from "react-icons/md";
import { UserContext } from "../contexts/UserContext";
import { FaFileUpload } from "react-icons/fa";
import CotizacionesNav from "./CotizacionesNav";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

// ─── Helpers ────────────────────────────────────────────────────────────────
const diasDesde = (fechaStr) => {
  if (!fechaStr) return "—";
  const fecha = new Date(fechaStr);
  const hoy = new Date();
  const diff = Math.floor((hoy - fecha) / (1000 * 60 * 60 * 24));
  return diff;
};

const diasColor = (dias) => {
  if (dias === "—") return { bg: "#f5f5f5", color: "#999" };
  if (dias <= 3) return { bg: "#d4f5e2", color: "#0a6e3f" };
  if (dias <= 7) return { bg: "#fff3cd", color: "#856404" };
  return { bg: "#fde8e8", color: "#a00" };
};

const formatFecha = (fechaStr) => {
  if (!fechaStr || fechaStr === "0000-00-00") return "—";
  const d = new Date(fechaStr);
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// ─── Estilos ─────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh",
    background: "#f7f4f6",
    padding: "32px 16px",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  header: {
    maxWidth: 1100,
    margin: "0 auto 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: "#121212",
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: "#888",
    marginTop: 2,
  },
  btnPrimary: {
    padding: "10px 22px",
    background: "#9f0059",
    color: "#fff",
    border: "none",
    borderRadius: 9,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "-0.2px",
  },
  card: {
    maxWidth: 1100,
    margin: "0 auto",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 2px 24px rgba(159,0,89,0.07)",
    overflow: "hidden",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    background: "#9f0059",
    color: "#fff",
    padding: "13px 16px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    textAlign: "left",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "13px 16px",
    fontSize: 13,
    color: "#222",
    borderBottom: "1px solid #f5f0f3",
    verticalAlign: "middle",
  },
  trEven: { background: "#fdf8fb" },
  trOdd: { background: "#fff" },
  diasBadge: (dias) => ({
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
    background: diasColor(dias).bg,
    color: diasColor(dias).color,
  }),
  accionesCel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "nowrap",
  },
  iconBtn: (color) => ({
    background: "none",
    border: "none",
    cursor: "pointer",
    color: color ?? "#555",
    padding: 4,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
  }),
  emptyRow: {
    textAlign: "center",
    padding: "48px 16px",
    color: "#aaa",
    fontSize: 14,
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    gap: 6,
    padding: "16px",
    borderTop: "1px solid #f0e6ec",
  },
  pageBtn: (active) => ({
    padding: "6px 12px",
    borderRadius: 7,
    border: "1.5px solid",
    borderColor: active ? "#9f0059" : "#e0d6dc",
    background: active ? "#9f0059" : "#fff",
    color: active ? "#fff" : "#9f0059",
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
  }),
  overlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    padding: "32px",
    width: "100%",
    maxWidth: 440,
    boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
  },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "#9f0059", marginBottom: 6 },
  modalSub: { fontSize: 13, color: "#666", marginBottom: 18 },
  label: { fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 6 },
  textarea: {
    width: "100%", minHeight: 90,
    padding: "10px 12px",
    border: "1.5px solid #e0d6dc",
    borderRadius: 8, fontSize: 14,
    fontFamily: "inherit", resize: "vertical",
    boxSizing: "border-box", outline: "none",
  },
  btnRow: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 },
  btnCancel: {
    padding: "9px 20px", background: "transparent",
    border: "1.5px solid #ccc", borderRadius: 8,
    fontSize: 14, cursor: "pointer", color: "#555",
  },
  btnConfirm: {
    padding: "9px 20px", background: "#9f0059",
    border: "none", borderRadius: 8,
    fontSize: 14, fontWeight: 700, cursor: "pointer", color: "#fff",
  },
};

// ─── Modal de rechazo ────────────────────────────────────────────────────────
const ModalRechazo = ({ orden, onConfirm, onCancel }) => {
  const [motivosOpts, setMotivosOpts] = useState([]);
  const [motivoSel,   setMotivoSel]   = useState("");
  const [motivoOtro,  setMotivoOtro]  = useState("");
  const [notas,       setNotas]       = useState("");

  useEffect(() => {
    axios.post(`${BASE}/motivos_rechazo_listar.php`)
      .then(r => setMotivosOpts(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const esOtro = motivoSel === "Otro";
  const motivoBase = esOtro ? motivoOtro.trim() : motivoSel;
  // Combinar motivo + notas adicionales en un solo texto
  const motivoFinal = motivoBase && notas.trim()
    ? `${motivoBase}\n\n${notas.trim()}`
    : motivoBase;

  const handleConfirm = () => {
    if (!motivoBase) { toast.error("El motivo es obligatorio."); return; }
    onConfirm(motivoFinal);
  };

  const sel = {
    width: "100%", padding: "9px 12px", border: "1.5px solid #e0d6dc",
    borderRadius: 8, fontSize: 13, outline: "none", background: "#fff",
    color: "#222", marginBottom: esOtro ? 10 : 0,
  };

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <p style={S.modalTitle}>Rechazar cotización</p>
        <p style={S.modalSub}>{orden.cliente} — {orden.proveedor}</p>
        <label style={S.label}>Motivo del rechazo *</label>
        <select style={sel} value={motivoSel} onChange={e => { setMotivoSel(e.target.value); setMotivoOtro(""); }} autoFocus>
          <option value="">— Seleccioná un motivo —</option>
          {motivosOpts.map(m => (
            <option key={m.id} value={m.motivo}>{m.motivo}</option>
          ))}
        </select>
        {esOtro && (
          <textarea
            style={S.textarea}
            placeholder="Describí el motivo..."
            value={motivoOtro}
            onChange={e => setMotivoOtro(e.target.value)}
          />
        )}
        <label style={{ ...S.label, marginTop: 12 }}>Notas adicionales (opcional)</label>
        <textarea
          style={{ ...S.textarea, minHeight: 70 }}
          placeholder="Contexto extra, por ejemplo: el cliente eligió a otro por precio, pidió un rediseño del packaging, etc."
          value={notas}
          onChange={e => setNotas(e.target.value)}
        />
        <div style={S.btnRow}>
          <button style={S.btnCancel} onClick={onCancel}>Cancelar</button>
          <button
            style={{ ...S.btnConfirm, opacity: !motivoFinal ? 0.5 : 1, cursor: !motivoFinal ? "not-allowed" : "pointer" }}
            onClick={handleConfirm}
            disabled={!motivoFinal}
          >
            Confirmar rechazo
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal de aceptación ─────────────────────────────────────────────────────
const _uid = () => Math.random().toString(36).slice(2);

const ModalAceptar = ({ orden, onConfirm, onCancel }) => {
  const [fecha,             setFecha]             = useState("");
  const [archivo,           setArchivo]           = useState(null);
  const [items,             setItems]             = useState([]);
  const [loadingItems,      setLoadingItems]      = useState(true);
  const [submitting,        setSubmitting]        = useState(false);
  const [entregasMultiples, setEntregasMultiples] = useState(false);
  const [bundles,           setBundles]           = useState([]);
  const [escalaSel,         setEscalaSel]          = useState({}); // { [itemId]: escalonElegido }

  // ¿Es ítem en escala?
  const esEscala = (it) => Number(it.es_escala) === 1 && Array.isArray(it.escalones) && it.escalones.length > 0;

  // Cantidad efectiva: para escala usa el tramo elegido; si no, la cantidad del ítem
  const cantEff = (it) =>
    esEscala(it)
      ? (parseFloat(escalaSel[it.id]?.cantidad) || 0)
      : (parseFloat(it.cantidad) || 0);

  // ¿Todos los ítems escala tienen un tramo elegido?
  const escalaCompleta = items.filter(esEscala).every(it => escalaSel[it.id]);

  // Cargar ítems de la orden al abrir
  useEffect(() => {
    if (!orden?.id) return;
    setLoadingItems(true);
    axios.post(`${BASE}/getOrderDetails.php`, new URLSearchParams({ orderId: orden.id }))
      .then(r => {
        const raw = r.data?.items ?? [];
        setItems(raw);
        // Inicializar Bundle 1 con todos los ítems al 100% de su cantidad
        // (los ítems escala arrancan en 0 hasta que se elija un tramo)
        setBundles([{
          _key: _uid(),
          fecha: "",
          cantidades: Object.fromEntries(raw.map(it =>
            [it.id, (Number(it.es_escala) === 1) ? 0 : (parseFloat(it.cantidad) || 0)]
          )),
        }]);
      })
      .catch(() => {})
      .finally(() => setLoadingItems(false));
  }, [orden?.id]);

  // Elegir tramo de un ítem escala → fija la cantidad efectiva en Bundle 1 y limpia el resto
  const selectEscalon = (itemId, escalon) => {
    setEscalaSel(prev => ({ ...prev, [itemId]: escalon }));
    const qty = parseFloat(escalon.cantidad) || 0;
    setBundles(prev => prev.map((b, idx) => ({
      ...b,
      cantidades: { ...b.cantidades, [itemId]: idx === 0 ? qty : 0 },
    })));
  };

  // ── Bundle helpers ─────────────────────────────────────────────────────
  const addBundle = () => setBundles(prev => [...prev, {
    _key: _uid(),
    fecha: "",
    cantidades: Object.fromEntries(items.map(it => [it.id, 0])),
  }]);

  const removeBundle = (key) => {
    if (bundles.length <= 1) return;
    setBundles(prev => prev.filter(b => b._key !== key));
  };

  const updateBundleFecha = (key, val) =>
    setBundles(prev => prev.map(b => b._key === key ? { ...b, fecha: val } : b));

  const updateBundleCantidad = (key, itemId, val) =>
    setBundles(prev => prev.map(b =>
      b._key === key ? { ...b, cantidades: { ...b.cantidades, [itemId]: val } } : b
    ));

  // ── Totales por ítem (para indicadores) ───────────────────────────────
  const sumaBundle = (itemId) =>
    bundles.reduce((s, b) => s + (parseFloat(b.cantidades[itemId]) || 0), 0);

  // ── Validación y submit ────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (submitting) return;
    if (!archivo) { toast.error("El archivo de orden de compra es obligatorio."); return; }
    if (!escalaCompleta) { toast.error("Elegí un tramo para cada ítem en escala."); return; }

    let fechaParaBackend, subEntregasData;

    if (entregasMultiples) {
      // Validar que todos los bundles tengan fecha
      for (const b of bundles) {
        if (!b.fecha) { toast.error("Todos los bundles deben tener fecha de entrega."); return; }
      }
      // Validar que las cantidades por ítem sumen a la cantidad efectiva
      for (const it of items) {
        const suma = sumaBundle(it.id);
        const qty  = cantEff(it);
        if (qty > 0 && Math.abs(suma - qty) > 0.001) {
          toast.error(`"${it.descripcion}": la suma de bundles (${suma}) no coincide con la cantidad (${qty}).`);
          return;
        }
      }
      // Convertir bundles → subEntregasData (formato del backend)
      subEntregasData = items.map(it => ({
        ordenes_id: it.id,
        sub_entregas: bundles
          .filter(b => (parseFloat(b.cantidades[it.id]) || 0) > 0)
          .map(b => ({ cantidad: parseFloat(b.cantidades[it.id]), fecha_entrega: b.fecha })),
      })).filter(e => e.sub_entregas.length > 0);
      // Fecha de la OC = la fecha más temprana de los bundles
      // (las fechas YYYY-MM-DD se ordenan cronológicamente con .sort())
      fechaParaBackend = bundles.map(b => b.fecha).filter(Boolean).sort()[0] || "";
    } else {
      if (!fecha) { toast.error("La fecha de entrega es obligatoria."); return; }
      subEntregasData  = [];
      fechaParaBackend = fecha;
    }

    // Tramos elegidos para ítems en escala (el backend sobrescribe cantidad/precio)
    const escalaSeleccion = items.filter(esEscala).map(it => ({
      ordenes_id: it.id,
      cantidad:   parseFloat(escalaSel[it.id].cantidad),
      precio:     parseFloat(escalaSel[it.id].precio),
    }));

    setSubmitting(true);
    try {
      await onConfirm(fechaParaBackend, archivo, subEntregasData, escalaSeleccion);
    } finally {
      setSubmitting(false);
    }
  };

  const inp = { padding: "5px 8px", border: "1.5px solid #e0d6dc", borderRadius: 6, fontSize: 13, background: "#fff", outline: "none" };

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, maxWidth: 680, maxHeight: "90vh", overflowY: "auto" }}>
        <p style={S.modalTitle}>Aceptar cotización</p>
        <p style={S.modalSub}>{orden.cliente} — {orden.proveedor}</p>

        {/* Archivo OC */}
        <label style={S.label}>Orden de compra (archivo) *</label>
        <label style={{
          display: "flex", alignItems: "center", gap: 8,
          border: "1.5px dashed #c990b0", borderRadius: 8, padding: "10px 14px",
          cursor: "pointer", color: "#9f0059", fontSize: 13, fontWeight: 600,
          background: "#fdf5fa", marginBottom: 18,
        }}>
          <FaFileUpload size={16} />
          {archivo ? archivo.name : "Seleccionar archivo..."}
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            style={{ display: "none" }}
            onChange={(e) => setArchivo(e.target.files[0] || null)} />
        </label>

        {/* Selección de tramo para ítems en escala */}
        {items.filter(esEscala).length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <label style={S.label}>Cotización en escala — elegí el tramo *</label>
            {items.filter(esEscala).map(it => (
              <div key={it.id} style={{ border: "1.5px solid #e0d6dc", borderRadius: 10, padding: "10px 12px", marginTop: 6 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: "#444", margin: "0 0 8px" }}>⚖ {it.descripcion}</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {it.escalones.map((esc, j) => {
                    const elegido = escalaSel[it.id] && Number(escalaSel[it.id].cantidad) === Number(esc.cantidad) && Number(escalaSel[it.id].precio) === Number(esc.precio);
                    return (
                      <button
                        key={j}
                        type="button"
                        onClick={() => selectEscalon(it.id, esc)}
                        style={{
                          fontSize: 12, fontWeight: 600, padding: "7px 12px", borderRadius: 8, cursor: "pointer",
                          border: elegido ? "1.5px solid #9f0059" : "1.5px solid #e0d6dc",
                          background: elegido ? "#fdf0f6" : "#fff",
                          color: elegido ? "#9f0059" : "#666",
                        }}
                      >
                        {esc.cantidad} u — ${(parseFloat(esc.precio) || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Toggle entregas múltiples */}
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 14 }}>
          <input
            type="checkbox"
            checked={entregasMultiples}
            onChange={(e) => setEntregasMultiples(e.target.checked)}
            style={{ width: 15, height: 15, accentColor: "#9f0059", cursor: "pointer" }}
          />
          <span style={{ fontSize: 13, fontWeight: 600, color: "#444" }}>Entregas múltiples</span>
        </label>

        {!entregasMultiples ? (
          /* Modo simple: una sola fecha */
          <div style={{ marginBottom: 18 }}>
            <label style={S.label}>Fecha de entrega *</label>
            <input
              type="date"
              style={{ ...S.textarea, minHeight: "unset", padding: "9px 12px", marginTop: 4 }}
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              autoFocus
            />
          </div>
        ) : (
          /* Modo bundles */
          loadingItems ? (
            <p style={{ fontSize: 13, color: "#aaa", textAlign: "center", marginBottom: 14 }}>Cargando ítems…</p>
          ) : (
            <div style={{ marginBottom: 18 }}>
              {bundles.map((b, bIdx) => (
                <div key={b._key} style={{ border: "1.5px solid #e0d6dc", borderRadius: 10, overflow: "hidden", marginBottom: 10 }}>
                  {/* Cabecera del bundle: fecha + botón eliminar */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#fdf0f6", borderBottom: "1px solid #f0e6ec" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#9f0059", minWidth: 72 }}>
                      📦 Bundle {bIdx + 1}
                    </span>
                    <input
                      type="date"
                      style={{ ...inp, flex: 1 }}
                      value={b.fecha}
                      onChange={(e) => updateBundleFecha(b._key, e.target.value)}
                    />
                    {bundles.length > 1 && (
                      <button
                        onClick={() => removeBundle(b._key)}
                        style={{ background: "none", border: "none", color: "#c0396a", cursor: "pointer", fontSize: 16, padding: "2px 4px", display: "flex", alignItems: "center" }}
                        title="Eliminar bundle"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {/* Cantidades por ítem */}
                  <div style={{ padding: "8px 12px" }}>
                    {items.map(it => (
                      <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: "#444", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {it.descripcion}
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          style={{ ...inp, width: 70, textAlign: "right" }}
                          value={b.cantidades[it.id] ?? 0}
                          onChange={(e) => updateBundleCantidad(b._key, it.id, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <button
                onClick={addBundle}
                style={{ fontSize: 12, background: "none", border: "1.5px dashed #c990b0", borderRadius: 7, color: "#9f0059", padding: "6px 14px", cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}
              >
                + Agregar bundle
              </button>

              {/* Indicadores de totales por ítem */}
              {items.length > 0 && (
                <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {items.map(it => {
                    const suma = sumaBundle(it.id);
                    const qty  = cantEff(it);
                    const ok   = qty === 0 || Math.abs(suma - qty) < 0.001;
                    return (
                      <span key={it.id} style={{
                        fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                        background: ok ? "#dcfce7" : "#fee2e2",
                        color: ok ? "#16a34a" : "#dc2626",
                      }}>
                        {it.descripcion.slice(0, 20)}{it.descripcion.length > 20 ? "…" : ""}: {suma}/{qty} {ok ? "✓" : "⚠"}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )
        )}

        <div style={S.btnRow}>
          <button
            style={{ ...S.btnCancel, opacity: submitting ? 0.5 : 1, cursor: submitting ? "not-allowed" : "pointer" }}
            onClick={onCancel}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            style={{ ...S.btnConfirm, opacity: (submitting || !escalaCompleta) ? 0.5 : 1, cursor: submitting ? "wait" : (!escalaCompleta ? "not-allowed" : "pointer") }}
            onClick={handleConfirm}
            disabled={submitting || !escalaCompleta}
          >
            {submitting ? "Procesando…" : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Componente principal ────────────────────────────────────────────────────
const OrdersAdmin = () => {
  const [orderToFetch, setOrderToFetch] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordenArechazar, setOrdenArechazar] = useState(null);
  const [ordenAaceptar, setOrdenAaceptar] = useState(null);
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroProveedor, setFiltroProveedor] = useState("");
  const itemsPerPage = 10;
  const history = useHistory();
  const isMounted = useRef(false);
  const { user } = useContext(UserContext);

  function traerOrders() {
    var xmlhttp1 = new XMLHttpRequest();
    xmlhttp1.onreadystatechange = function () {
      if (xmlhttp1.readyState === 4 && xmlhttp1.status === 200) {
        let respuesta1 = xmlhttp1.responseText;
        if (respuesta1 === "Debe iniciar Sesion") { history.push("/"); return; }
        try {
          const itemsArray = JSON.parse(respuesta1);
          if (Array.isArray(itemsArray) && isMounted.current) {
            setOrderToFetch(itemsArray.filter(o => !o.estado || o.estado === "pendiente"));
          }
        } catch (error) { console.error("Error parsing JSON:", error); }
      }
    };
    xmlhttp1.open("POST", `${process.env.REACT_APP_API_BASE||"http://localhost/twinpack/php"}/buscar_solicitudes_nuevas.php`, true);
    xmlhttp1.setRequestHeader("Content-type", "application/x-www-form-urlencoded");
    xmlhttp1.send("Search=&Filter=&Sort=id&SelectedPage=1");
  }

  useEffect(() => {
    isMounted.current = true;
    traerOrders();
    return () => { isMounted.current = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAceptar = (orden) => setOrdenAaceptar(orden);

  const handleConfirmarAceptar = async (fechaEntrega, archivo, subEntregasData = [], escalaSeleccion = []) => {
    try {
      const formData = new FormData();
      formData.append("orden_id",        ordenAaceptar.id);
      formData.append("accion",          "aceptar");
      formData.append("fechaEntrega",    fechaEntrega);
      formData.append("ordenCompra",     archivo);
      formData.append("usuario_id",      user?.id ?? 0);
      formData.append("subEntregasData", JSON.stringify(subEntregasData));
      formData.append("escalaSeleccion", JSON.stringify(escalaSeleccion));
      const res = await axios.post(`${process.env.REACT_APP_API_BASE||"http://localhost/twinpack/php"}/actualizar_cotizacion.php`, formData);
      if (res.data.success) {
        toast.success("Cotización aceptada");
        setOrdenAaceptar(null);
        traerOrders();
      } else {
        toast.error(res.data.mensaje || "Error al aceptar");
      }
    } catch { toast.error("Error de conexión"); }
  };

  const handleConfirmarRechazo = async (motivo) => {
    try {
      const formData = new FormData();
      formData.append("orden_id", ordenArechazar.id);
      formData.append("accion", "rechazar");
      formData.append("motivo", motivo);
      formData.append("usuario_id", user?.id ?? 0);
      const res = await axios.post(`${process.env.REACT_APP_API_BASE||"http://localhost/twinpack/php"}/actualizar_cotizacion.php`, formData);
      if (res.data.success) {
        toast.success("Cotización rechazada");
        setOrdenArechazar(null);
        traerOrders();
      } else toast.error(res.data.mensaje || "Error al rechazar");
    } catch { toast.error("Error de conexión"); }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("¿Eliminar esta orden?")) return;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE||"http://localhost/twinpack/php"}/eliminar_orden.php`,
        new URLSearchParams({ id: orderId })
      );
      if (response.data.status === "success") { traerOrders(); toast.success(`Orden ${orderId} eliminada`); }
      else toast.error("Error al eliminar la orden");
    } catch { toast.error("Error al eliminar la orden"); }
  };

  // ── Filtros por cliente / proveedor ──────────────────────────────────────
  const clientesUnicos = [...new Set(orderToFetch.map(o => o.cliente).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const proveedoresUnicos = [...new Set(orderToFetch.map(o => o.proveedor).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const ordersFiltradas = orderToFetch.filter(o =>
    (!filtroCliente   || o.cliente   === filtroCliente) &&
    (!filtroProveedor || o.proveedor === filtroProveedor)
  );

  useEffect(() => { setCurrentPage(1); }, [filtroCliente, filtroProveedor]);

  const filtroSelect = {
    padding: "8px 12px", border: "1.5px solid #e0d6dc", borderRadius: 8,
    fontSize: 13, color: "#333", background: "#fff", outline: "none", cursor: "pointer",
  };

  const paginatedOrders = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return ordersFiltradas.slice(startIndex, startIndex + itemsPerPage);
  };

  const totalPages = Math.ceil(ordersFiltradas.length / itemsPerPage);

  return (
    <section style={S.page}>
      <CotizacionesNav />
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Cotizaciones pendientes</h2>
          <p style={S.subtitle}>
            {ordersFiltradas.length} cotización{ordersFiltradas.length !== 1 ? "es" : ""}
            {(filtroCliente || filtroProveedor) ? ` de ${orderToFetch.length}` : ""} esperando respuesta
          </p>
        </div>
        <button style={S.btnPrimary} onClick={() => history.push("/dashboard/newquotation")}>
          + Nueva cotización
        </button>
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <select value={filtroCliente} onChange={(e) => setFiltroCliente(e.target.value)} style={filtroSelect}>
          <option value="">Todos los clientes</option>
          {clientesUnicos.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filtroProveedor} onChange={(e) => setFiltroProveedor(e.target.value)} style={filtroSelect}>
          <option value="">Todos los proveedores</option>
          {proveedoresUnicos.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        {(filtroCliente || filtroProveedor) && (
          <button
            onClick={() => { setFiltroCliente(""); setFiltroProveedor(""); }}
            style={{ background: "none", border: "none", color: "#9f0059", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            Limpiar filtros
          </button>
        )}
      </div>

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Fecha cotización", "Días", "Cliente", "Proveedor", "Observaciones", "Monto", "Acciones"].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedOrders().length === 0 ? (
              <tr><td colSpan={7} style={S.emptyRow}>No hay cotizaciones pendientes 🎉</td></tr>
            ) : (
              paginatedOrders().map((order, i) => {
                const dias = diasDesde(order.fecha);
                return (
                  <tr key={order.id} style={i % 2 === 0 ? S.trOdd : S.trEven}>
                    <td style={S.td}>{formatFecha(order.fecha)}</td>
                    <td style={S.td}>
                      <span style={S.diasBadge(dias)}>
                        {dias === "—" ? "—" : `${dias}d`}
                      </span>
                    </td>
                    <td style={{ ...S.td, fontWeight: 600 }}>{order.cliente}</td>
                    <td style={S.td}>{order.proveedor}</td>
                    <td style={{ ...S.td, color: "#666", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {order.observaciones || "—"}
                    </td>
                    <td style={{ ...S.td, fontWeight: 600 }}>
                      {order.monto_total ? (
                        <span style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                          <span>
                            {order.moneda === "USD" ? "USD " : "$"}
                            {parseFloat(order.monto_total).toLocaleString("es-AR")}
                          </span>
                          {order.moneda === "USD" && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 5px",
                              borderRadius: 20, background: "#fffbeb", color: "#92400e", border: "1px solid #fcd34d" }}>
                              USD
                            </span>
                          )}
                        </span>
                      ) : "—"}
                    </td>
                    <td style={S.td}>
                      <div style={S.accionesCel}>
                        <button style={S.iconBtn("#555")} title="Editar"
                          onClick={() => history.push(`/dashboard/newquotation/${order.id}`)}>
                          <FaRegEdit size={16} />
                        </button>
                        <button style={S.iconBtn("#c0392b")} title="Generar PDF"
                          onClick={() => history.push({ pathname: "/dashboard/presupuesto", state: { orderId: order.id, totalAmount: order.monto_total } })}>
                          <FaFilePdf size={16} />
                        </button>
                        <button style={S.iconBtn("#0a6e3f")} title="Aceptar" onClick={() => handleAceptar(order)}>
                          <MdCheck size={20} />
                        </button>
                        <button style={S.iconBtn("#9f0059")} title="Rechazar" onClick={() => setOrdenArechazar(order)}>
                          <MdClose size={20} />
                        </button>
                        <button style={S.iconBtn("#c00")} title="Eliminar" onClick={() => handleDeleteOrder(order.id)}>
                          <MdDelete size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div style={S.pagination}>
            {Array.from({ length: totalPages }, (_, i) => (
              <button key={i} style={S.pageBtn(currentPage === i + 1)} onClick={() => setCurrentPage(i + 1)}>
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {ordenArechazar && (
        <ModalRechazo
          orden={ordenArechazar}
          onConfirm={handleConfirmarRechazo}
          onCancel={() => setOrdenArechazar(null)}
        />
      )}

      {ordenAaceptar && (
        <ModalAceptar
          orden={ordenAaceptar}
          onConfirm={handleConfirmarAceptar}
          onCancel={() => setOrdenAaceptar(null)}
        />
      )}
    </section>
  );
};

export default OrdersAdmin;
